import React, { useEffect, useMemo, useRef } from "react";
import { useGLTF, Text, RenderTexture, OrthographicCamera } from "@react-three/drei";
import { Mesh, Texture, MeshStandardMaterial } from "three";
import { useFrame, useThree } from "@react-three/fiber";
import { degresToRad } from "../../../helpers/angles";

interface GlbEngravingInsoleViewProps {
  textRef: React.MutableRefObject<string>;
  isLeft: boolean;
  insoleUri: string;
  engravingInsoleMeshName: string;
}

export const GlbEngravingInsoleView = ({ textRef, isLeft, insoleUri, engravingInsoleMeshName }: GlbEngravingInsoleViewProps) => {
  const { scene } = useGLTF(insoleUri);
  const { invalidate } = useThree();

  const renderTexRef = useRef<Texture | null>(null);
  const textA = useRef<any>(null);
  const textB = useRef<any>(null);
  const materialRef = useRef<MeshStandardMaterial | null>(null);
  const shaderMeshRef = useRef<Mesh | null>(null);
  const textureInitialized = useRef(false);

  // Clone the scene AND clone every material so this mount owns its own
  // GPU-bound state. drei's useGLTF cache shares gltf.scene across remounts,
  // so any direct mutation would compound shaders/uniforms across navigations
  // (the previous "shaderSetup" flag hack tried to work around this). With a
  // fresh clone per mount we can safely dispose materials in the cleanup
  // effect below.
  const clonedScene = useMemo(() => {
    const cloned = scene.clone(true);
    cloned.traverse((obj) => {
      if (!(obj instanceof Mesh)) return;
      if (Array.isArray(obj.material)) return;
      const clonedMat = obj.material.clone() as MeshStandardMaterial;
      clonedMat.vertexColors = false;
      clonedMat.needsUpdate = true;
      obj.material = clonedMat;
    });
    return cloned;
  }, [scene]);

  // Add engraving shader customization to the engraving mesh
  useEffect(() => {
    clonedScene.traverse((obj) => {
      if (!(obj instanceof Mesh)) return;
      if (obj.name !== engravingInsoleMeshName) return;

      const material = obj.material as MeshStandardMaterial;

      // Set custom cache key to ensure unique shader compilation per side.
      material.customProgramCacheKey = () => {
        return `engraving-${isLeft ? 'left' : 'right'}-${engravingInsoleMeshName}`;
      };

      material.onBeforeCompile = (shader) => {
        shader.uniforms.mapText = { value: renderTexRef.current };
        shader.uniforms.uFlipUvX = { value: isLeft ? 0.0 : 1.0 };

        // Add UV1 attribute and varying in vertex shader
        shader.vertexShader = shader.vertexShader.replace(
          "void main() {",
          `
            varying vec2 vUv1;
            uniform float uFlipUvX;

            void main() {
            vUv1 = uv1;

            // move engraving down in UV space (not render space)
            vUv1.y += 0.24;
          `
        );

        shader.fragmentShader = shader.fragmentShader.replace(
          "void main() {",
          `
            uniform sampler2D mapText;
            varying vec2 vUv1;

            void main() {
          `
        );

        // Apply engraving effect using UV1
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <opaque_fragment>",
          `
            // Apply text engraving using UV1
            vec4 engravingTex = texture2D(mapText, vUv1);
            vec3 engravingEffect = outgoingLight * outgoingLight;
            outgoingLight = mix(outgoingLight, engravingEffect, engravingTex.r);

            #include <opaque_fragment>
          `
        );

        // store for useFrame updates
        (obj as any).userData.shader = shader;
      };

      shaderMeshRef.current = obj;
      materialRef.current = material;

      material.needsUpdate = true;
    });
  }, [clonedScene, isLeft, engravingInsoleMeshName]);

  // Dispose cloned materials on unmount so the renderer can release the
  // associated GLSL programs and uniforms. Geometry and textures live in
  // drei's useGLTF cache and are NOT cloned by scene.clone(true), so we leave
  // them alone — the next mount will reuse them.
  useEffect(() => {
    const localClonedScene = clonedScene;
    return () => {
      localClonedScene.traverse((obj) => {
        if (!(obj instanceof Mesh)) return;
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      });
      shaderMeshRef.current = null;
      materialRef.current = null;
      textureInitialized.current = false;
    };
  }, [clonedScene]);

  // Force material update when RenderTexture is ready
  useEffect(() => {
    if (renderTexRef.current && materialRef.current) {
      materialRef.current.needsUpdate = true;
    }
  }, [renderTexRef.current]);

  // 3) Update text & uniforms each frame
  useFrame(() => {
    // Force material update when texture first becomes available
    if (renderTexRef.current && !textureInitialized.current && materialRef.current) {
      materialRef.current.needsUpdate = true;
      textureInitialized.current = true;
    }

    // sync RenderTexture into shader uniform using cached mesh reference
    if (shaderMeshRef.current?.userData.shader) {
      shaderMeshRef.current.userData.shader.uniforms.mapText.value = renderTexRef.current;
    }

    if (textA.current) {
      textA.current.text = textRef.current ?? "";
      invalidate();
    }
    if (textB.current) {
      textB.current.text = textRef.current ?? "";
      invalidate();
    }
  });

  // Positioning in main scene
  const position = isLeft
  ? [-0.0062, -0.002, 0.0008]
  : [-0.0062, -0.002, 0];

  const rotation: [number, number, number] = isLeft ? [degresToRad(-5), degresToRad(95), 0] : [degresToRad(-5), degresToRad(85), 0];

  return (
    <>
      {/* GLB Model */}
      <group position={position as any} rotation={rotation} scale={1}>
        <primitive object={clonedScene} />
      </group>

      {/* RenderTexture used as engraving mask */}
      <mesh visible={false}>
        <planeGeometry />
        <meshStandardMaterial>
          <RenderTexture ref={renderTexRef} attach="map" anisotropy={16}>
            <OrthographicCamera makeDefault manual
              left={-0.5}
              right={0.5}
              top={0.3}
              bottom={-0.7}
              near={0.1}
              far={100}
              position={[0, 0, 5]} />

            <color attach="background" args={["black"]} />

            <Text
              ref={textA}
              position={[0, -0.25, 0]}
              letterSpacing={0.1}
              fontSize={0.11}
              color="#777"
              outlineOffsetX={'3%'}
              outlineOffsetY={'3%'}
              outlineBlur={'4%'}
              outlineWidth={'4%'}
              outlineOpacity={1}
              outlineColor="#bbb"
            >
              .
            </Text>
            <Text
              ref={textB}
              position={[0, -0.25, 0]}
              letterSpacing={0.1}
              fontSize={0.11}
              color="#777"
              outlineOffsetX={'-3%'}
              outlineOffsetY={'-3%'}
              outlineBlur={'4%'}
              outlineWidth={'4%'}
              outlineOpacity={1}
              outlineColor="#000"
            >
              .
            </Text>
          </RenderTexture>
        </meshStandardMaterial>
      </mesh>
    </>
  );
};
